// Open-now / live status helpers. All computations use Aiken's timezone (ET).
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function toMin(s) {
  if (!s || !s.includes(":")) return null;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
}

function fmt12(min) {
  if (min == null) return "";
  let h = Math.floor(min / 60) % 24;
  const m = min % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

function etNow() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const wd = (get("weekday") || "").toLowerCase();
  let hh = parseInt(get("hour") || "0", 10);
  if (hh === 24) hh = 0;
  const mm = parseInt(get("minute") || "0", 10);
  const isoDate = `${get("year")}-${get("month")}-${get("day")}`;
  return { wd, cur: hh * 60 + mm, isoDate };
}

export function todayKey() {
  return etNow().wd;
}

export function dayLabel(h) {
  if (!h || h.closed) return "Closed";
  if (!h.open) return "—";
  const o = toMin(h.open), c = toMin(h.close);
  if (o == null) return "—";
  return c == null ? fmt12(o) : `${fmt12(o)} – ${fmt12(c)}`;
}

function humanMins(m) {
  if (m == null) return "";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r === 0 ? `${h} hr` : `${h} hr ${r} min`;
}

// hours: weekly {day: DayHours}. special: {isoDate: DayHours} overrides today.
export function getOpenStatus(hours, special) {
  const { wd, cur, isoDate } = etNow();
  const map = hours || {};
  const override = special ? special[isoDate] : undefined;
  if (!override && Object.keys(map).length === 0) return { status: "unknown" };
  // Treat a placeholder week (every day marked closed with no open time) as "hours not listed",
  // not a genuine 7-day closure — otherwise unenriched listings all read as "Closed".
  const _keys = Object.keys(map);
  const _anyOpen = _keys.some((k) => map[k] && !map[k].closed && map[k].open);
  if (!override && _keys.length > 0 && !_anyOpen) return { status: "unknown" };
  const idx = DAY_ORDER.indexOf(wd);
  const today = override || map[wd];
  const prev = map[DAY_ORDER[(idx + 6) % 7]];
  const holiday = override ? { holiday: true, holidayNote: override.note || "Special hours" } : {};

  const openResult = (closeMin, closesInMin) => {
    const closingSoon = closesInMin != null && closesInMin > 0 && closesInMin <= 60;
    const soonText = closesInMin != null ? `Closes in ${humanMins(closesInMin)}` : undefined;
    return {
      status: "open",
      label: closingSoon ? soonText : "Open now",
      closingSoon,
      closesInMin,
      soonText,
      detail: closeMin != null ? `until ${fmt12(closeMin % 1440)}` : undefined,
      today: dayLabel(today),
      ...holiday,
    };
  };

  if (today && !today.closed && today.open) {
    const o = toMin(today.open), c = toMin(today.close);
    if (o != null && c != null) {
      if (c > o) {
        if (cur >= o && cur < c) return openResult(c, c - cur);
      } else if (cur >= o) {
        return openResult(c, c + 1440 - cur);
      }
    } else if (o != null && c == null && cur >= o) {
      return openResult(null, null);
    }
  }

  if (!override && prev && !prev.closed && prev.open && prev.close) {
    const o = toMin(prev.open), c = toMin(prev.close);
    if (o != null && c != null && c <= o && cur < c) return openResult(c, c - cur);
  }

  let detail, opensInMin, openingSoon;
  if (today && !today.closed && today.open) {
    const o = toMin(today.open);
    if (o != null && cur < o) {
      opensInMin = o - cur;
      openingSoon = opensInMin <= 60;
      detail = openingSoon ? `Opens in ${humanMins(opensInMin)}` : `Opens ${fmt12(o)}`;
    }
  }
  return { status: "closed", label: "Closed", detail, opensInMin, openingSoon, today: dayLabel(today), ...holiday };
}

export function isOpenNow(place) {
  return getOpenStatus(place?.hours, place?.special_hours).status === "open";
}

// Open past 9 PM today (or overnight into tomorrow).
export function isOpenLate(place) {
  const { wd, isoDate } = etNow();
  const special = place?.special_hours || {};
  const h = special[isoDate] || place?.hours?.[wd];
  if (!h || h.closed || !h.open || !h.close) return false;
  const o = toMin(h.open), c = toMin(h.close);
  if (o == null || c == null) return false;
  return c <= o || c >= 21 * 60;
}

// Rough popular-times heuristic by category + day/hour. Only shown when open.
const BUSY_PEAKS = {
  "Food & Drink": [[12, 13], [18, 20]],
  "Entertainment": [[19, 22]],
  "Outdoors": [[9, 11], [16, 18]],
  "Attractions": [[10, 13]],
  "Equestrian": [[8, 11]],
  "Arts & Culture": [[11, 14]],
  "Shopping": [[12, 16]],
  "Church": [[9, 12]],
};
const BUSY_MAP = {
  quiet: { text: "Usually quiet right now", color: "text-green-600", dot: "bg-green-500" },
  moderate: { text: "Moderately busy right now", color: "text-amber-600", dot: "bg-amber-500" },
  soon: { text: "Gets busy soon", color: "text-amber-600", dot: "bg-amber-500" },
  busy: { text: "Usually busy right now", color: "text-red-500", dot: "bg-red-400" },
};

export function getBusyHint(place) {
  const status = getOpenStatus(place?.hours, place?.special_hours);
  if (status.status !== "open") return null;
  const { wd, cur } = etNow();
  const hour = Math.floor(cur / 60);
  const peaks = BUSY_PEAKS[place?.category] || [[12, 14]];
  const weekend = wd === "sat" || wd === "sun";
  let level = "quiet";
  for (const [a, b] of peaks) {
    if (hour >= a && hour < b) { level = "busy"; break; }
    if (hour >= a - 1 && hour < a) level = "soon";
  }
  if (level === "quiet" && weekend && hour >= 11 && hour <= 20) level = "moderate";
  return { level, ...BUSY_MAP[level] };
}
